if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          thumbnailsList: '[id^="Slider-Thumbnails"]',
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.zoomDelegateBound = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      getInitialVariant() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      revealGallery() {
        this.style.opacity = 1;
        this.removeAttribute('data-initializing');
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        // Variant gallery filtering relies on Liquid passing both the product-level fallback media
        // and the set of images/variants that belong to sibling variant media.
        this.productFeaturedImageId = this.dataset.productFeaturedImageId;
        this.productFeaturedImageSrc = this.dataset.productFeaturedImageSrc;
        this.variantWithImageIds = JSON.parse(this.dataset.variantWithImageIds || '[]').map(String);
        this.variantImageSources = JSON.parse(this.dataset.variantImages || '[]');
        this.variantImageIds = JSON.parse(this.dataset.variantImageIds || '[]').map(String);
        this.cacheMediaEntries();
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        const initialVariant = this.getInitialVariant();
        if (initialVariant) {
          this.setActiveMedia(initialVariant);
        }

        this.revealGallery();
        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        this.setupLightbox();

        if (this.zoomDelegateBound) return;

        this.zoomDelegateBound = true;
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      setupLightbox() {
        if (this.lightbox) {
          this.lightbox.destroy();
        }

        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();
      }

      cacheMediaEntries() {
        if (this.mediaEntries) return;

        // Cache the original gallery/thumbnails once so variant changes can re-render from a stable source.
        const mediaItems = Array.from(this.elements.mediaList?.children || []);
        const thumbnailItems = Array.from(this.elements.thumbnailsList?.children || []);

        this.mediaEntries = mediaItems.map((mediaItem) => {
          const mediaId = String(mediaItem.dataset.mediaId);

          return {
            id: mediaId,
            mediaItem,
            thumbnailItem: thumbnailItems.find((thumbnail) => String(thumbnail.dataset.target) === mediaId),
          };
        });
      }

      isVariantMedia(mediaItem) {
        const mediaImageId = mediaItem.dataset.mediaImageId;
        const mediaSrc = mediaItem.dataset.mediaSrc;
        // Some catalogs expose variant image linkage by image id, others only by src.
        const isVariantImageId = mediaImageId && this.variantImageIds.includes(mediaImageId);
        const isVariantImageSrc = mediaSrc && this.variantImageSources.includes(mediaSrc);

        return isVariantImageId || isVariantImageSrc;
      }

      getMediaFromEntry(entry) {
        if (!entry) return null;

        return {
          id: entry.id,
          position: Number(entry.mediaItem.dataset.mediaIndex) || 0,
          media_type: entry.mediaItem.dataset.mediaType || 'image',
        };
      }

      getProductFeaturedMedia() {
        this.cacheMediaEntries();

        const featuredEntry = (this.mediaEntries || []).find(({ mediaItem }) => {
          return (
            (this.productFeaturedImageId && mediaItem.dataset.mediaImageId === this.productFeaturedImageId) ||
            (this.productFeaturedImageSrc && mediaItem.dataset.mediaSrc === this.productFeaturedImageSrc)
          );
        });

        return this.getMediaFromEntry(featuredEntry);
      }

      getFeaturedMediaFromVariant(variant) {
        if (!variant) return this.getProductFeaturedMedia();
        // Variants without their own image should fall back to the product featured image in the gallery.
        if (!this.variantWithImageIds.includes(String(variant.id))) return this.getProductFeaturedMedia();

        if (variant.featured_media) {
          return variant.featured_media;
        }

        if (variant.featured_image) {
          return {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        return this.getProductFeaturedMedia();
      }

      getVisibleMediaEntries(featuredMediaId) {
        return (this.mediaEntries || []).filter(({ id, mediaItem }) => {
          return !this.isVariantMedia(mediaItem) || id === featuredMediaId;
        });
      }

      renderMediaEntries(entries, featuredMediaId) {
        const orderedEntries = [...entries];

        // Non-slider layouts need the selected variant media moved to the front manually.
        if (!this.sliderInstance?.slider) {
          const featuredIndex = orderedEntries.findIndex(({ id }) => id === featuredMediaId);
          if (featuredIndex > 0) {
            const [featuredEntry] = orderedEntries.splice(featuredIndex, 1);
            orderedEntries.unshift(featuredEntry);
          }
        }

        this.elements.mediaList.innerHTML = '';
        orderedEntries.forEach(({ mediaItem }) => {
          this.elements.mediaList.appendChild(mediaItem);
        });

        if (this.elements.thumbnailsList) {
          this.elements.thumbnailsList.innerHTML = '';
          orderedEntries.forEach(({ thumbnailItem }) => {
            if (thumbnailItem) {
              this.elements.thumbnailsList.appendChild(thumbnailItem);
            }
          });
        }

        orderedEntries.forEach(({ mediaItem }, index) => {
          mediaItem.dataset.mediaIndex = index;
          const zoomButton = mediaItem.querySelector('.js-photoswipe--zoom');
          if (zoomButton) {
            zoomButton.dataset.mediaIndex = index;
          }
        });

        return orderedEntries;
      }

      filterVariantMedia(featuredMedia) {
        this.cacheMediaEntries();

        const featuredMediaId = String(featuredMedia.id);
        const visibleEntries = this.getVisibleMediaEntries(featuredMediaId);
        const orderedEntries = this.renderMediaEntries(visibleEntries, featuredMediaId);
        const selectedIndex = Math.max(
          orderedEntries.findIndex(({ id }) => id === featuredMediaId),
          0,
        );

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.update();
          this.sliderInstance.slider.slideTo(selectedIndex, 0, false);
        }

        if (this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.update();
          this.thumbsInstance.slider.slideTo(selectedIndex, 0, false);
        }

        this.updateActiveThumbnail(selectedIndex);

        if (this.enableImageZoom) {
          // Rebuild the lightbox datasource so zoom only includes the currently visible media.
          this.setupLightbox();
        }

        return selectedIndex;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          this.updateActiveThumbnail(activeIndex);

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      updateActiveThumbnail(activeIndex) {
        if (!this.elements.thumbnailsList) return;

        // Re-rendering the thumbnail list can break Swiper's active class bookkeeping, so we
        // reapply the active border manually after filtering or slide changes.
        this.elements.thumbnailsList.querySelectorAll('.swiper-slide').forEach((thumbnail, index) => {
          thumbnail.classList.toggle('swiper-slide-thumb-active', index === activeIndex);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        const featuredMedia = this.getFeaturedMediaFromVariant(variant);

        if (!featuredMedia) return;

        if (this.sliderInstance?.slider) {
          this.filterVariantMedia(featuredMedia);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia = this.getFeaturedMediaFromVariant(variant)) {
        if (!featuredMedia) return;

        this.filterVariantMedia(featuredMedia);

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
